import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
} from "@mui/material";
import { Search, Clear, DateRange } from "@mui/icons-material";
import { useGetList } from "../../hooks/useGetList";
import { useDispatch, useSelector } from "react-redux";
import {
  clearFilter,
  setFilterType,
  setSearch,
} from "../../redux/refundRequest/filterSlice";
import { debounce } from "../../utils/debounce";
import buildParams from "../../utils/buildParams";

const RefundHeader = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [branch, setBranch] = useState("");
  const [status, setStatus] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const filter = useSelector((state) => state.refundRequest);

  // const { loading, data } = useGetList(`branches`);
  const dispatch = useDispatch();

  // Reset filter on component mount to ensure clean initial state
  useEffect(() => {
    dispatch(clearFilter());
  }, [dispatch]);

 

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((value) => {
      dispatch(setSearch(value));
    }, 300),
    [dispatch],
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setShowClearButton(value.length > 0);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setShowClearButton(false);
    dispatch(setSearch(""));
  };

  const handleBranchChange = (event) => {
    dispatch(setFilterType({ relatedRefundBranch: event.target.value }));
    setBranch(event.target.value);
  };

  const handleStatusChange = (event) => {
    dispatch(setFilterType({ status: event.target.value }));
  };

  const handleDateSearch = () => {
    // Handle date search logic here
    dispatch(
      setFilterType({
        startDate: startDate,
        endDate: endDate,
      }),
    );
    console.log("Searching with dates:", { startDate, endDate });
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    dispatch(setFilterType({ startDate: "", endDate: "" }));
  };

  return (
    <Box sx={{ marginBottom: 3, marginTop: 3 }}>
      {/* Date Range Section */}
      <Card
        sx={{ mb: 3, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <DateRange sx={{ fontSize: 24, color: "text.secondary" }} />
            <Typography variant="h6" component="div" fontWeight={600}>
              Date Range Filter
            </Typography>
          </Stack>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleDateSearch}
                  sx={{ minWidth: 120 }}
                >
                  Search
                </Button>
                {startDate && endDate && (
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClearDates}
                    sx={{ minWidth: 100 }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search and Filters Section */}
      <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            component="div"
            gutterBottom
            fontWeight={600}
          >
            Search & Filters
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search refunds..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                  endAdornment: showClearButton && (
                    <IconButton
                      onClick={handleClearSearch}
                      size="small"
                      sx={{ color: "text.secondary" }}
                    >
                      <Clear />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="branch-select-label">Branch</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={branch}
                  label="Branch"
                  onChange={handleBranchChange}
                >
                  <MenuItem value="">
                    <em>All Branches</em>
                  </MenuItem>
                  {!loading &&
                    data?.list?.map((branch) => (
                      <MenuItem value={branch?._id}> {branch?.name} </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={filter?.filterType?.status}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="">
                    <em>All Status</em>
                  </MenuItem>
                  <MenuItem value="Finished">Finished</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Declined">Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RefundHeader;
